import { MAINTENANCE_LABELS } from './maintenance.js';
import type { BillingLine, BillingSummaryOutput, BillingTotals } from './schemas/billing.js';

/**
 * Council n°2 (08/10/2026) : formats du récapitulatif de facturation, partagés par l'interface et les tests.
 * - texte collable : cellules séparées par des tabulations (collage dans un tableur ou un logiciel) ;
 * - CSV : séparateur `;`, BOM UTF-8, guillemets doublés.
 * Dans les deux, une cellule qui commencerait par `=`, `+`, `-` ou `@` est neutralisée (apostrophe en tête).
 * Montants en décimal exact reçus de l'API, affichés avec une virgule et sans symbole (« 1234,50 »).
 */

const LINE_HEADER = [
  'Section',
  'Libellé',
  'Catégorie',
  'Référence',
  'Quantité',
  'Prix unitaire HT',
  'TVA %',
  'Total HT',
  'Total TVA',
  'Total TTC',
];

/** « 1234.50 » → « 1234,50 » ; vide si absent. */
export function billingNumber(value: string | null): string {
  return value === null ? '' : value.replace('.', ',');
}

/** « 3.000 » → « 3 », « 1.500 » → « 1,5 », « 20.00 » → « 20 ». */
function trimDecimal(value: string): string {
  const [int = '0', dec = ''] = value.split('.');
  const d = dec.replace(/0+$/, '');
  return d ? `${int},${d}` : int;
}

/** `AAAA-MM-JJ` → `JJ/MM/AAAA`. */
function frDate(key: string | null): string {
  if (!key) return '';
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
}

const neutralize = (v: string) => (/^[=+\-@\t\r]/.test(v) ? `'${v}` : v);

function lineRow(section: string, l: BillingLine): string[] {
  return [
    section,
    l.label,
    l.maintenanceCategory
      ? MAINTENANCE_LABELS[l.maintenanceCategory]
      : l.kind === 'labor'
        ? 'Main-d’œuvre'
        : 'Pièce',
    l.reference ?? '',
    trimDecimal(l.quantity),
    billingNumber(l.unitPriceHt),
    trimDecimal(l.vatRate),
    billingNumber(l.totalHt),
    billingNumber(l.totalVat),
    billingNumber(l.totalTtc),
  ];
}

const totalsRow = (label: string, t: BillingTotals) => [
  label,
  '',
  '',
  '',
  '',
  '',
  '',
  billingNumber(t.totalHt),
  billingNumber(t.totalVat),
  billingNumber(t.totalTtc),
];

/** Tableau du récapitulatif (cellules brutes), commun aux deux formats. */
export function billingSummaryRows(s: BillingSummaryOutput): string[][] {
  const rows: string[][] = [
    ['Récapitulatif pour facturation'],
    ['Plaque', s.vehicle.plate],
    ['Véhicule', `${s.vehicle.make} ${s.vehicle.model}`.trim()],
    ['Kilométrage entrée', String(s.vehicle.entryMileage)],
    ['Kilométrage sortie', s.vehicle.exitMileage === null ? '' : String(s.vehicle.exitMileage)],
    ['Client', s.customer.name],
    ['Raison sociale', s.customer.companyName ?? ''],
    ['Date d’entrée', frDate(s.receivedOn)],
    ['Date de sortie', frDate(s.releasedOn)],
    [],
    LINE_HEADER,
  ];
  for (const section of s.sections) {
    const name =
      section.kind === 'complement'
        ? `${section.title} (version ${section.complementVersion ?? ''} acceptée)`
        : section.title;
    for (const l of section.lines) rows.push(lineRow(name, l));
    rows.push(totalsRow(`Sous-total ${name}`, section.totals));
  }
  rows.push([]);
  for (const v of s.vatBreakdown) {
    rows.push([
      `TVA ${trimDecimal(v.vatRate)} %`,
      '',
      '',
      '',
      '',
      '',
      '',
      billingNumber(v.totalHt),
      billingNumber(v.totalVat),
      '',
    ]);
  }
  rows.push(totalsRow('Total', s.totals));
  if (s.unpricedLines > 0) {
    rows.push([`Attention : ${s.unpricedLines} ligne(s) sans prix, exclue(s) des totaux`]);
  }
  return rows;
}

/** Texte collable : une ligne par rangée, cellules séparées par des tabulations. */
export function billingSummaryText(s: BillingSummaryOutput): string {
  return billingSummaryRows(s)
    .map((r) => r.map((c) => neutralize(c.replace(/[\t\r\n]+/g, ' '))).join('\t'))
    .join('\n');
}

/** Cellule CSV (`;`) : formule neutralisée, guillemets doublés si besoin. */
export function billingCsvCell(value: string): string {
  const safe = neutralize(value);
  return /[";\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** CSV (`;`, BOM UTF-8, fins de ligne CRLF) pour un tableur français. */
export function billingSummaryCsv(s: BillingSummaryOutput): string {
  return `${String.fromCharCode(0xfeff)}${billingSummaryRows(s)
    .map((r) => r.map(billingCsvCell).join(';'))
    .join('\r\n')}\r\n`;
}
